// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { getDocs, query, collection, startAfter, orderBy, where, limit } from 'firebase/firestore'

export async function getCameraTimestampFromBaleFeedTimestamp(
    firestore,
    client_id,
    device_id,
    baleFeedTimestamp
) {
    const materialFlowCollection = collection(firestore, 'clients', client_id, 'devices', device_id, 'material_flow_timestamps')
    const q = query(
        materialFlowCollection,
        where('start_timestamp_at_bale_feed', '<=', baleFeedTimestamp),
        orderBy('start_timestamp_at_bale_feed', 'desc'),
        limit(1)
    )
    const materialFlowDocs = await getDocs(q)
    if (materialFlowDocs.empty) {
        return null
    }
    
    const materialFlowDoc = materialFlowDocs.docs[0].data()

    // const closestPosition = findClosestPositionToTimestamp(materialFlowDoc.bale_feed_position_timestamps, baleFeedTimestamp)
    const convertedCameraTimestamp = convertTimestamps(materialFlowDoc.bale_feed_position_timestamps, materialFlowDoc.position_timestamps, baleFeedTimestamp)
    if (!convertedCameraTimestamp) {
        return null
    }

    return convertedCameraTimestamp
}

// Return the position with the closest timestamp to the target timestamp
function findClosestPositionToTimestamp(posTimestampDict, targetTimestamp) {
    if (!posTimestampDict || Object.keys(posTimestampDict).length === 0) {
        return null;
    }
    
    let closestKey = null;
    let minDifference = Infinity;
    
    for (const [pos, timestamp] of Object.entries(posTimestampDict)) {
        const difference = Math.abs(timestamp.toDate().getTime() - targetTimestamp.getTime());
        
        if (difference < minDifference) {
            minDifference = difference;
            closestKey = pos;
        }
    }
    
    return closestKey;
}

/**
 * Converts a timestamp from one reference system to another using linear interpolation
 * @param {Object.<string, Timestamp>} dictA - Dictionary with positions as keys and timestamps as values for system A
 * @param {Object.<string, Timestamp>} dictB - Dictionary with the same positions as keys but different timestamps for system B
 * @param {Date|Timestamp} timestampA - The timestamp in system A to convert to system B
 * @returns {Date|null} - The equivalent timestamp in system B, or null if conversion is not possible
 */
export function convertTimestamps(dictA, dictB, timestampA) {
    if (!dictA || !dictB || Object.keys(dictA).length === 0 || Object.keys(dictB).length === 0) {
        return null;
    }
    
    // Ensure we have a JavaScript Date object to work with
    const timestampAMs = timestampA.toDate ? timestampA.toDate().getTime() : timestampA.getTime();
    
    // Get all positions as numbers and sort them
    const positions = Object.keys(dictA).map(Number).sort((a, b) => a - b);
    
    // Find the bracketing positions for timestampA
    let posLow = null;
    let posHigh = null;
    let timestampLowMs = null;
    let timestampHighMs = null;
    
    // Find bracketing positions
    for (let i = 0; i < positions.length - 1; i++) {
        const pos1 = positions[i].toString();
        const pos2 = positions[i + 1].toString();
        
        const time1 = dictA[pos1].toDate ? dictA[pos1].toDate().getTime() : dictA[pos1].getTime();
        const time2 = dictA[pos2].toDate ? dictA[pos2].toDate().getTime() : dictA[pos2].getTime();
        
        // Check if timestampA is between these two times
        if (time1 <= timestampAMs && timestampAMs <= time2) {
            posLow = positions[i];
            posHigh = positions[i + 1];
            timestampLowMs = time1;
            timestampHighMs = time2;
            break;
        }
    }
    
    // If we couldn't find bracketing positions, return null
    if (posLow === null || posHigh === null) {
        return null;
    }
    
    // Linear interpolation to find the exact position corresponding to timestampA
    const posRange = posHigh - posLow;
    const timeRange = timestampHighMs - timestampLowMs;
    const positionInterpolated = posLow + (posRange * (timestampAMs - timestampLowMs) / timeRange);
    
    // Get timestamps from dictB
    if (!dictB[posLow] || !dictB[posHigh]) {
        return null;
    }
    
    const timeBLowMs = dictB[posLow].toDate ? dictB[posLow].toDate().getTime() : dictB[posLow].getTime();
    const timeBHighMs = dictB[posHigh].toDate ? dictB[posHigh].toDate().getTime() : dictB[posHigh].getTime();
    
    // Linear interpolation to find the exact timestamp in system B
    const fraction = (positionInterpolated - Math.floor(positionInterpolated));
    const timestampBMs = timeBLowMs + (fraction * (timeBHighMs - timeBLowMs));
    const timestampB = new Date(timestampBMs);
    
    return timestampB;
}



